import { useCallback } from 'react';

import { graphql, useMutation } from 'react-relay';

import { buildMutationError } from '../../utils/mutation-errors';

import type {
	mainHubCreateMutation,
	mainHubCreateMutation$data,
} from './__generated__/mainHubCreateMutation.graphql';
import type {
	mainHubUpdateMutation,
	mainHubUpdateMutation$data,
} from './__generated__/mainHubUpdateMutation.graphql';

export interface UsePageManagementProps {
	createCustomPage: (params: {
		clonePageAri?: string;
		connectionId: string;
		description: string | null;
		helpCenterAri: string;
		name: string;
	}) => Promise<mainHubCreateMutation$data>;
	updateCustomPage: (params: {
		description: string | null;
		helpCenterPageAri: string;
		name: string;
	}) => Promise<mainHubUpdateMutation$data>;
}

export interface UsePageManagementOptions {
	cloudId: string;
}

export const usePageManagement = ({
	cloudId,
}: UsePageManagementOptions): UsePageManagementProps => {
	const [pageManagementCreateMutation] = useMutation<mainHubCreateMutation>(graphql`
		mutation mainHubCreateMutation(
			$cloudId: ID!
			$input: HelpCenterPageCreateInput!
			$connections: [ID!]!
		) {
			helpCenter(cloudId: $cloudId) {
				createHelpCenterPage(input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
					helpCenterPage
						@appendNode(connections: $connections, edgeTypeName: "HelpCenterPageQueryResultEdge") {
						id
						name {
							default
						}
						description {
							default
						}
						helpCenterAri
						updatedAt
					}
				}
			}
		}
	`);

	const [pageManagementUpdateMutation] = useMutation<mainHubUpdateMutation>(graphql`
		mutation mainHubUpdateMutation($cloudId: ID!, $input: HelpCenterPageUpdateInput!) {
			helpCenter(cloudId: $cloudId) {
				updateHelpCenterPage(input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
					helpCenterPage {
						id
						name {
							default
						}
						description {
							default
						}
						helpCenterAri
						updatedAt
					}
				}
			}
		}
	`);

	const createCustomPage = useCallback(
		({
			name,
			description,
			connectionId,
			clonePageAri,
			helpCenterAri,
		}: {
			clonePageAri?: string;
			connectionId: string;
			description: string | null;
			helpCenterAri: string;
			name: string;
		}) => {
			return new Promise<mainHubCreateMutation$data>((resolve, reject) =>
				pageManagementCreateMutation({
					variables: {
						cloudId,
						input: {
							helpCenterAri,
							name,
							description,
							clonePageAri,
						},
						connections: [connectionId],
					},
					onCompleted(response) {
						if (
							response.helpCenter?.createHelpCenterPage?.success &&
							response.helpCenter?.createHelpCenterPage?.helpCenterPage?.id
						) {
							return resolve(response);
						}

						const errors = response.helpCenter?.createHelpCenterPage?.errors;
						return reject(buildMutationError(errors));
					},
					onError: (error) => reject(error),
				}),
			);
		},
		[cloudId, pageManagementCreateMutation],
	);

	const updateCustomPage = useCallback(
		({
			name,
			description,
			helpCenterPageAri,
		}: {
			description: string | null;
			helpCenterPageAri: string;
			name: string;
		}) => {
			return new Promise<mainHubUpdateMutation$data>((resolve, reject) =>
				pageManagementUpdateMutation({
					variables: {
						cloudId,
						input: {
							helpCenterPageAri,
							name,
							description,
						},
					},
					onCompleted(response) {
						if (
							response.helpCenter?.updateHelpCenterPage?.success &&
							response.helpCenter?.updateHelpCenterPage?.helpCenterPage?.id
						) {
							return resolve(response);
						}

						const errors = response.helpCenter?.updateHelpCenterPage?.errors;
						return reject(buildMutationError(errors));
					},
					onError: (error) => reject(error),
				}),
			);
		},
		[cloudId, pageManagementUpdateMutation],
	);

	return { createCustomPage, updateCustomPage };
};
